import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ClipboardCheck, TrendingUp, TrendingDown, AlertTriangle, MapPin, Shield, BookOpen, CheckCircle2, FileText, Activity } from 'lucide-react'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid } from 'recharts'
import { useAuth } from '../../context/AuthContext'
import { useHasCapability } from '../../hooks/useCapability'
import { getComplianceTrends } from '../../lib/services/dashboards'
import { getCorrectiveActionSummary } from '../../lib/services/dashboards'
import { getAssignments } from '../../lib/services/visits'
import { getCorrectiveActions } from '../../lib/services/correctiveActions'
import { listTrainingAssignments } from '../../lib/services/training'
import { getReports } from '../../lib/services/reports'
import { cmsStatusTone } from '../../lib/services/scoringPresentation'
import { getDailyPlan, getRotationLabel } from '../../lib/services/dailyAssessment'
import { calculateProgress, getOperatingDaysElapsed } from '../../lib/services/dailyAssessmentProgress'
import { getFieldAppUrl } from '../../lib/fieldAppHost'
import type { Visit } from '../../lib/services/visits'

type DailyAssessmentCtaState = {
  label: 'Begin Daily Assessment' | 'Resume Daily Assessment'
  statusText: 'Daily Assessment Not Started' | 'Daily Assessment Started' | 'Daily Assessment Finished'
}

function toDateKey(value: Date): string {
  return value.toISOString().slice(0, 10)
}

function readLocalDailyStatus(facilityId: string | null | undefined, date: Date): 'started' | 'finished' | null {
  if (typeof window === 'undefined' || !facilityId) return null
  const key = `daily-assessment-status:${facilityId}:${toDateKey(date)}`
  const value = window.localStorage.getItem(key)
  return value === 'started' || value === 'finished' ? value : null
}

/**
 * Draft presence = assessment in progress. If the surveyor captured any
 * photo or recorded any response the DailyAssessment page persists it
 * under `daily-assessment-draft:<facility>:<date>`. That's a stronger
 * in-progress signal than the status flag alone because the flag can
 * be out of sync if an early bail happened before setResponse fired.
 */
function toDateKeyFromOffset(anchor: Date, daysBack: number): string {
  const d = new Date(anchor)
  d.setDate(d.getDate() - daysBack)
  return toDateKey(d)
}

/**
 * Walk the last `days` days of per-day daily-assessment-status keys in
 * localStorage and count how many are 'finished'. Days that were never
 * attempted (no key set) are counted toward the denominator so the
 * resulting percentage reflects "of the last N days, how often was the
 * daily completed?" — a compliance driver, not a best-case sampler.
 */
function countFinishedWithinWindow(
  facilityId: string | null | undefined,
  anchor: Date,
  days: number,
  daysBackStart = 0,
): { finished: number; total: number } {
  if (typeof window === 'undefined' || !facilityId) return { finished: 0, total: 0 }
  let finished = 0
  for (let i = daysBackStart; i < daysBackStart + days; i += 1) {
    const key = `daily-assessment-status:${facilityId}:${toDateKeyFromOffset(anchor, i)}`
    if (window.localStorage.getItem(key) === 'finished') finished += 1
  }
  return { finished, total: days }
}

type DailyComplianceSummary = {
  last30Pct: number
  last30Label: string          // e.g., "22 of 30 days"
  trend: 'up' | 'down' | 'flat' // current 30d window vs 90-120d window
  trendDeltaPct: number         // current - prior, in percentage points
}

function summarizeDailyCompliance(
  facilityId: string | null | undefined,
  anchor: Date,
): DailyComplianceSummary {
  const current = countFinishedWithinWindow(facilityId, anchor, 30, 0)
  const prior = countFinishedWithinWindow(facilityId, anchor, 30, 90)
  const currentPct = current.total > 0 ? Math.round((current.finished / current.total) * 100) : 0
  const priorPct = prior.total > 0 ? Math.round((prior.finished / prior.total) * 100) : 0
  const delta = currentPct - priorPct
  const trend: 'up' | 'down' | 'flat' = delta >= 3 ? 'up' : delta <= -3 ? 'down' : 'flat'
  return {
    last30Pct: currentPct,
    last30Label: `${current.finished} of ${current.total} days`,
    trend,
    trendDeltaPct: delta,
  }
}

function hasLocalDailyDraft(facilityId: string | null | undefined, date: Date): boolean {
  if (typeof window === 'undefined' || !facilityId) return false
  const key = `daily-assessment-draft:${facilityId}:${toDateKey(date)}`
  const raw = window.localStorage.getItem(key)
  if (!raw) return false
  try {
    const parsed = JSON.parse(raw) as { responses?: unknown[]; photoEvidence?: unknown[] }
    if (Array.isArray(parsed.responses) && parsed.responses.length > 0) return true
    if (Array.isArray(parsed.photoEvidence) && parsed.photoEvidence.length > 0) return true
    return false
  } catch {
    return false
  }
}

function getDailyAssessmentCtaState(
  visits: Visit[],
  viewerUserId: string | undefined,
  facilityId: string | null | undefined,
  date: Date,
): DailyAssessmentCtaState {
  const dateKey = toDateKey(date)
  const todayDailyVisit = visits.find((visit) => {
    if (visit.visit_type !== 'daily_self_assessment') return false
    if (facilityId && visit.facility_id !== facilityId) return false
    return toDateKey(new Date(visit.visit_date)) === dateKey
  })

  const localStatus = readLocalDailyStatus(facilityId, date)
  const hasDraft = hasLocalDailyDraft(facilityId, date)
  const isAssignedToViewer = Boolean(todayDailyVisit?.assigned_surveyor_id && todayDailyVisit.assigned_surveyor_id === viewerUserId)

  if (todayDailyVisit?.status === 'released' || localStatus === 'finished') {
    return { label: 'Begin Daily Assessment', statusText: 'Daily Assessment Finished' }
  }

  if (todayDailyVisit?.status === 'in_progress') {
    return {
      label: isAssignedToViewer ? 'Resume Daily Assessment' : 'Begin Daily Assessment',
      statusText: 'Daily Assessment Started',
    }
  }

  // Either the explicit 'started' flag OR persisted draft evidence
  // means the surveyor has work to resume.
  if (localStatus === 'started' || hasDraft) {
    return { label: 'Resume Daily Assessment', statusText: 'Daily Assessment Started' }
  }

  return { label: 'Begin Daily Assessment', statusText: 'Daily Assessment Not Started' }
}

export function StandaloneDashboard() {
  const { viewerContext } = useAuth()
  const [trendWindowDays, setTrendWindowDays] = useState(90)
  const role = viewerContext?.role ?? 'Surveyor'
  const canRunAssessment = useHasCapability('run_assessment')
  const showStaffView = !canRunAssessment

  const trendsQuery = useQuery({
    queryKey: ['dashboard-trends', trendWindowDays],
    queryFn: () => getComplianceTrends(trendWindowDays),
    staleTime: 5 * 60 * 1000,
  })

  const capaQuery = useQuery({
    queryKey: ['dashboard-capa'],
    queryFn: () => getCorrectiveActionSummary(),
    staleTime: 2 * 60 * 1000,
  })

  const visitsQuery = useQuery({
    queryKey: ['dashboard-visits'],
    queryFn: getAssignments,
    staleTime: 2 * 60 * 1000,
  })

  const myActionsQuery = useQuery({
    queryKey: ['dashboard-my-actions'],
    queryFn: () => getCorrectiveActions(),
    staleTime: 2 * 60 * 1000,
    enabled: showStaffView,
  })

  const myTrainingQuery = useQuery({
    queryKey: ['dashboard-my-training'],
    queryFn: listTrainingAssignments,
    staleTime: 2 * 60 * 1000,
  })

  const reportsQuery = useQuery({
    queryKey: ['dashboard-reports'],
    queryFn: getReports,
    staleTime: 2 * 60 * 1000,
  })

  // Baseline check: has at least one released visit (consultant-led initial assessment)
  const visits = visitsQuery.data ?? []
  const hasBaseline = visits.some(v => v.status === 'released')
  const primaryFacilityId = viewerContext?.facility_ids?.[0] ?? null

  // Today's daily assessment plan
  const today = new Date()
  const dailyPlan = getDailyPlan(today)
  const dailyCompliance = summarizeDailyCompliance(primaryFacilityId, today)
  const dailyAssessmentCta = getDailyAssessmentCtaState(
    visits,
    viewerContext?.user_id,
    primaryFacilityId,
    today,
  )

  const latest = trendsQuery.data?.[trendsQuery.data.length - 1]
  const previous = trendsQuery.data?.[trendsQuery.data.length - 2]
  const scoreDelta = latest && previous ? latest.overall_score - previous.overall_score : 0
  const latestTone = latest ? cmsStatusTone(latest.cms_outcome) : { text: 'text-slate-600', bg: 'bg-slate-100' }

  const capa = capaQuery.data ?? { open: 0, in_progress: 0, completed: 0, verified: 0, overdue: 0 }
  const totalOpen = capa.open + capa.in_progress
  const trainingAssignments = myTrainingQuery.data ?? []
  const openTrainingCount = trainingAssignments.filter(a => a.assignment_state !== 'completed' && a.assignment_state !== 'cancelled').length
  const overdueTrainingCount = trainingAssignments.filter(a => a.due_date && new Date(a.due_date) < new Date() && a.assignment_state !== 'completed' && a.assignment_state !== 'cancelled').length
  const reports = reportsQuery.data ?? []
  const pendingReports = reports.filter(r => r.status === 'draft').length

  // Daily assessment progress
  const progress = calculateProgress(visitsQuery.data ?? [], getOperatingDaysElapsed())
  const dailyCurrentness = dailyPlan
    ? `${Math.min(progress.rotationCompleted, progress.rotationTotal)} of ${progress.rotationTotal || dailyPlan.items.length}`
    : 'Off today'

  // QAPI trending data — format for recharts
  const trendPoints = (trendsQuery.data ?? []).map(pt => ({
    date: new Date(pt.period_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    score: pt.overall_score,
    outcome: pt.cms_outcome,
  }))
  const lowestTrend = trendsQuery.data?.reduce((lowest, point) => point.overall_score < lowest.overall_score ? point : lowest, trendsQuery.data[0])
  const trendDirection = scoreDelta > 0 ? 'Improving' : scoreDelta < 0 ? 'Needs attention' : 'Flat'

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      {/* Greeting */}
      <section className="v11-workspace-header">
        <p className="text-xs font-bold uppercase tracking-wider text-[var(--brand-secondary)]">
          Daily Compliance Overview
        </p>
        <h1 className="mt-1 font-brand text-2xl font-bold text-[var(--brand-deep)]">
          Welcome back{showStaffView ? '' : `, ${role}`}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {showStaffView ? 'Your tasks, training, and facility overview.' : 'Your facility readiness snapshot.'}
        </p>
      </section>

      {/* Staff: Assigned Tasks + Training | Others: Daily Assessment CTA */}
      {showStaffView ? (
        <>
          {/* My Assigned Tasks */}
          <section className="v11-section">
            <div className="v11-section-header">
              <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">My Tasks</h2>
              <Link to="/actions" className="text-xs font-bold text-[var(--brand-primary)] hover:underline">View All</Link>
            </div>
            {(() => {
              const userId = viewerContext?.user_id
              const actions = (myActionsQuery.data ?? [])
                .filter(a => a.assigned_to_user_id === userId && a.status !== 'verified' && a.status !== 'completed')
                .slice(0, 5)
              if (actions.length === 0) {
                return <p className="p-5 text-sm text-slate-400">No open tasks assigned to you.</p>
              }
              return (
                <ul className="space-y-2 p-2">
                  {actions.map(a => {
                    const isOverdue = a.due_date && new Date(a.due_date) < new Date()
                    return (
                      <li key={a.action_id}>
                        <Link to={`/actions/${a.action_id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 transition-colors hover:border-[var(--brand-primary)] hover:bg-slate-50">
                          <CheckCircle2 size={16} className={isOverdue ? 'text-rose-500' : 'text-amber-500'} />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-slate-800">{a.title}</p>
                            <p className="text-xs text-slate-400">
                              {a.status === 'in_progress' ? 'In Progress' : 'Open'}
                              {a.due_date && <> &middot; Due {new Date(a.due_date).toLocaleDateString()}</>}
                            </p>
                          </div>
                          {isOverdue && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-600">Overdue</span>}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              )
            })()}
          </section>

          {/* My Training */}
          <section className="v11-section">
            <div className="v11-section-header">
              <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">My Training</h2>
              <Link to="/training" className="text-xs font-bold text-[var(--brand-primary)] hover:underline">View All</Link>
            </div>
            {(() => {
              const assignments = (myTrainingQuery.data ?? [])
                .filter(a => a.assignment_state !== 'completed' && a.assignment_state !== 'cancelled')
                .slice(0, 5)
              if (assignments.length === 0) {
                return <p className="p-5 text-sm text-slate-400">No pending training assignments.</p>
              }
              return (
                <ul className="space-y-2 p-2">
                  {assignments.map(a => {
                    const isOverdue = a.due_date && new Date(a.due_date) < new Date()
                    return (
                      <li key={a.assignment_id}>
                        <Link to="/training" className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 transition-colors hover:border-[var(--brand-primary)] hover:bg-slate-50">
                          <BookOpen size={16} className={isOverdue ? 'text-rose-500' : 'text-[var(--brand-secondary)]'} />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-slate-800">{a.education_modules?.title ?? 'Training Module'}</p>
                            <p className="text-xs text-slate-400">
                              {a.assignment_state === 'in_progress' ? 'In Progress' : 'Assigned'}
                              {a.due_date && <> &middot; Due {new Date(a.due_date).toLocaleDateString()}</>}
                            </p>
                          </div>
                          {isOverdue && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-600">Overdue</span>}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              )
            })()}
          </section>
        </>
      ) : !hasBaseline ? (
        <section className="overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--brand-deep)] via-[var(--brand-primary)] to-[#1a6b9a] p-6">
          <div className="flex items-center gap-3">
            <Shield size={24} className="shrink-0 text-[var(--brand-accent)]" />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--brand-accent)]">
                Baseline Assessment Required
              </p>
              <h2 className="mt-1 font-brand text-lg font-bold text-white">
                Schedule your initial guided survey
              </h2>
              <p className="mt-1 text-sm text-white/55">
                Your consultant will conduct a full CMS Appendix L assessment to establish your baseline compliance score. Daily self-assessments begin after the baseline is complete.
              </p>
            </div>
          </div>
        </section>
      ) : (
        <section className="overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--brand-deep)] via-[var(--brand-primary)] to-[#1a6b9a] p-6">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--brand-accent)]">
              Daily Self-Assessment
            </p>
            {dailyPlan ? (
              <>
                <h2 className="mt-1 font-brand text-lg font-bold text-white">
                  Today: {getRotationLabel(dailyPlan.rotationDay)}
                </h2>
                <p className="mt-1 flex items-center gap-2 text-sm text-white/55">
                  <MapPin size={12} />
                  {dailyPlan.combinedAreas.join(' + ')} &middot; {dailyPlan.items.length} items &middot; ~{dailyPlan.estimatedMinutes} min
                </p>
                <p className="mt-1 text-xs text-white/40">
                  Domains: {dailyPlan.domains.join(', ')}
                </p>
                <p className="mt-3 text-xs font-bold uppercase tracking-wider text-white/70">
                  {dailyAssessmentCta.statusText}
                </p>
                {/*
                 * Compliance driver: % of last 30 days with a completed
                 * daily assessment, plus an arrow comparing the current
                 * 30-day window with the one 3 months ago (~4 months of
                 * signal). Arrow uses ±3pp hysteresis to avoid flapping.
                 */}
                <div className="mt-3 flex items-end gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/55">
                      Last 30 days
                    </p>
                    <p className="font-brand text-2xl font-extrabold text-white">
                      {dailyCompliance.last30Pct}%
                    </p>
                    <p className="text-[11px] font-semibold text-white/55">
                      {dailyCompliance.last30Label}
                    </p>
                  </div>
                  <div className={`mb-0.5 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${
                    dailyCompliance.trend === 'up'
                      ? 'bg-emerald-400/20 text-emerald-100'
                      : dailyCompliance.trend === 'down'
                        ? 'bg-red-400/20 text-red-100'
                        : 'bg-white/10 text-white/70'
                  }`}
                    title="Current 30-day window vs the window from 3 months ago"
                  >
                    {dailyCompliance.trend === 'up' && <TrendingUp size={12} />}
                    {dailyCompliance.trend === 'down' && <TrendingDown size={12} />}
                    {dailyCompliance.trend === 'flat' && <span>—</span>}
                    <span>
                      {dailyCompliance.trendDeltaPct > 0 ? '+' : ''}
                      {dailyCompliance.trendDeltaPct}pp vs 4 mo ago
                    </span>
                  </div>
                </div>
                <a
                  href={getFieldAppUrl('/self-monitoring/daily')}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[var(--brand-accent)] px-5 py-2.5 text-sm font-bold text-[#1a1000] transition-all hover:brightness-110"
                >
                  <ClipboardCheck size={16} />
                  {dailyAssessmentCta.label}
                </a>
              </>
            ) : (
              <h2 className="mt-1 font-brand text-lg font-bold text-white">
                No assessment today (non-operating day)
              </h2>
            )}
          </div>
        </section>
      )}

      {/* Summary Cards */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
        <div className="v11-kpi-card v11-kpi-accent-teal">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Compliance direction</p>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-brand text-3xl font-extrabold text-[var(--brand-deep)]">
              {latest?.overall_score ?? '—'}
            </span>
            {scoreDelta !== 0 && (
              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${scoreDelta > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                {scoreDelta > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {scoreDelta > 0 ? '+' : ''}{scoreDelta}
              </span>
            )}
          </div>
          <p className={`mt-1 text-xs font-semibold ${latestTone.text}`}>
            {latest?.cms_outcome_label ?? 'No data'}
          </p>
        </div>

        <div className="v11-kpi-card v11-kpi-accent-gold">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Daily currentness</p>
          <span className="mt-2 block font-brand text-3xl font-extrabold text-[var(--brand-deep)]">{dailyCurrentness}</span>
          <p className="mt-1 text-xs text-slate-500">{dailyPlan ? 'Current rotation' : 'No daily plan'}</p>
        </div>

        <div className="v11-kpi-card v11-kpi-accent-gold">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Open Actions</p>
          <span className="mt-2 block font-brand text-3xl font-extrabold text-[var(--brand-deep)]">{totalOpen}</span>
          <p className="mt-1 text-xs text-slate-500">{capa.in_progress} in progress</p>
        </div>

        <div className="v11-kpi-card v11-kpi-accent-rose">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">QAPI attention</p>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-brand text-3xl font-extrabold text-[var(--brand-deep)]">{capa.overdue}</span>
            {capa.overdue > 0 && <AlertTriangle size={16} className="text-rose-500" />}
          </div>
          <p className="mt-1 text-xs text-slate-500">{capa.overdue > 0 ? 'Action required' : 'All clear'}</p>
        </div>

        <div className="v11-kpi-card v11-kpi-accent-teal">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Training currentness</p>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-brand text-3xl font-extrabold text-[var(--brand-deep)]">{openTrainingCount}</span>
            {overdueTrainingCount > 0 ? <AlertTriangle size={16} className="text-rose-500" /> : <BookOpen size={16} className="text-[var(--brand-secondary)]" />}
          </div>
          <p className="mt-1 text-xs text-slate-500">{overdueTrainingCount > 0 ? `${overdueTrainingCount} overdue` : 'Assigned or in progress'}</p>
        </div>

        <div className="v11-kpi-card v11-kpi-accent-gold">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Reports pending</p>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-brand text-3xl font-extrabold text-[var(--brand-deep)]">{pendingReports}</span>
            <FileText size={16} className="text-[var(--brand-primary)]" />
          </div>
          <p className="mt-1 text-xs text-slate-500">Drafts awaiting review or release</p>
        </div>
      </section>

      <section className="v11-section">
        <div className="v11-section-header">
          <div>
            <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Trend summary</h2>
            <p className="mt-1 text-xs font-semibold text-slate-500">{trendWindowDays}-day compliance readout</p>
          </div>
          <select
            aria-label="Dashboard trend range"
            value={trendWindowDays}
            onChange={(event) => setTrendWindowDays(Number(event.target.value))}
            className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700"
          >
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={180}>180 days</option>
            <option value={365}>365 days</option>
          </select>
        </div>
        <div className="grid gap-3 p-5 sm:grid-cols-3">
          <div className="v11-mini-metric p-3">
            <p className="text-xs font-bold uppercase text-slate-400">Direction</p>
            <p className="mt-1 flex items-center gap-2 font-brand text-lg font-extrabold text-[var(--brand-deep)]">
              <Activity size={16} className={scoreDelta < 0 ? 'text-rose-500' : 'text-[var(--brand-secondary)]'} />
              {trendDirection}
            </p>
          </div>
          <div className="v11-mini-metric p-3">
            <p className="text-xs font-bold uppercase text-slate-400">Latest score</p>
            <p className="mt-1 font-brand text-lg font-extrabold text-[var(--brand-deep)]">{latest?.overall_score ?? '—'}</p>
          </div>
          <div className="v11-mini-metric p-3">
            <p className="text-xs font-bold uppercase text-slate-400">Lowest score</p>
            <p className="mt-1 font-brand text-lg font-extrabold text-[var(--brand-deep)]">{lowestTrend?.overall_score ?? '—'}</p>
          </div>
        </div>
      </section>

      {/* QAPI Compliance Trend — daily assessment scores over time */}
      <section className="v11-section">
        <div className="v11-section-header">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">QAPI Compliance Trend</h2>
          {hasBaseline && (
            <span className="text-xs font-bold text-slate-400">
              Rotation: {progress.rotationCompleted}/{progress.rotationTotal}
            </span>
          )}
        </div>
        {trendPoints.length >= 2 ? (
          <div className="h-52 p-5">
            <div className="mb-3 flex justify-end">
              <span className="rounded bg-slate-100 px-2 py-1 text-[11px] font-bold text-slate-500">{trendWindowDays} days</span>
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendPoints} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                  formatter={(value) => [`${value}%`, 'Score']}
                />
                <ReferenceLine y={95} stroke="#10b981" strokeDasharray="4 4" label={{ value: 'Meets Standard', position: 'right', fontSize: 9, fill: '#10b981' }} />
                <ReferenceLine y={85} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: 'Conditional', position: 'right', fontSize: 9, fill: '#f59e0b' }} />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="var(--brand-primary)"
                  strokeWidth={2.5}
                  dot={{ fill: 'var(--brand-primary)', r: 3 }}
                  activeDot={{ r: 5, fill: 'var(--brand-deep)' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="m-5 flex h-32 items-center justify-center rounded-xl border border-dashed border-slate-200 text-center">
            <p className="text-sm text-slate-400">
              {trendPoints.length === 1
                ? 'One data point recorded. Trend chart appears after the second assessment.'
                : 'No assessment data yet. Complete daily assessments to see your compliance trend.'}
            </p>
          </div>
        )}
      </section>

      {/* Annual Progress */}
      {hasBaseline && progress.annualCompleted > 0 && (
        <section className="v11-section">
          <div className="v11-section-header">
            <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Annual Coverage</h2>
            <span className="font-brand text-sm font-bold text-[var(--brand-deep)]">{progress.annualPercent}%</span>
          </div>
          <div className="p-5">
            <div className="flex items-center gap-4">
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[var(--brand-secondary)] to-teal-400 transition-all"
                  style={{ width: `${progress.annualPercent}%` }}
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              {progress.annualCompleted} of ~{progress.annualTotal} operating days completed
            </p>
          </div>
        </section>
      )}
    </div>
  )
}
